import React, { useEffect, useState } from "react";
import Card from "components/Card/Card";

import {
  Text,
  Flex,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Button,
  Textarea,
  RadioGroup,
  Stack,
  SimpleGrid,
  AbsoluteCenter,
  Center,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useColorMode,
  useColorModeValue,
  Skeleton,
} from "@chakra-ui/react";
import { Field, Form, Formik } from "formik";
import CustomCheckbox from "../../../components/Checkbox/CustomCheckbox";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import axios from "axios";
import apiUrl from "../../../variables/api";
import darkStyles from "variables/darkStyles";
import Creatable from "react-select/creatable";

import locations from "variables/locations";

import { format, toZonedTime } from 'date-fns-tz';

const refreshDateTime = () => {
  const now = new Date();
  const timeZone = 'Europe/Warsaw';
  const polandTime = toZonedTime(now, timeZone);
  return format(polandTime, "yyyy-MM-dd'T'HH:mm");
};

const initialValues = {
  dateTime: refreshDateTime(),
  location: "",
  transferPerson: "",
  transferItem: "",
  transferItemName: "",
  transferWarings: "",
  transferSeizing: "",
};

const validate = (values) => {
  const errors = {};

  if (!values.dateTime) {
    errors.dateTime = "Musisz podać datę i godzinę";
  }

  if (!values.location) {
    errors.location = "Musisz podać lokalizację";
  }

  if (!values.transferPerson) {
    errors.transferPerson = "Musisz wybrać cel przekazania";
  }

  if (!values.transferItem) {
    errors.transferItem = "Musisz wybrać co przekazujesz";
  }

  if (!values.transferItemName) {
    errors.transferItemName = "Musisz podać szczegóły przekazywanego obiektu";
  }

  if (!values.transferWarings) {
    errors.transferWarings = "Musisz podać uwagi dotyczące zabezpieczenia";
  }

  if (!values.transferSeizing) {
    errors.transferSeizing = "Musisz podać kto przejmuje";
  }

  return errors;
};

function Page() {
  const { colorMode } = useColorMode();

  const [formData, setFormData] = useState(initialValues);
  const formikRef = React.useRef();
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    console.log("Dane formularza", values);
    await setFormData(values);
    console.log("Wysyłanie formularza", formData);

    axios
      .post(apiUrl + "/data/transferConfirm", values, {
        headers: { "x-access-token": localStorage.getItem("token") },
      })
      .then((response) => {
        if (response.data.success) {
          formikRef.current.resetForm();
          formikRef.current.setFieldValue("dateTime", refreshDateTime());
          formikRef.current.setSubmitting(false);
          return toast.success("Pomyślnie wysłano potwierdzenie przekazania");
        } else {
          formikRef.current.setSubmitting(false);
          return toast.error(
            "Napotkano błąd podczas wysyłania potwierdzenia przekazania"
          );
        }
      })
      .catch((error) => {
        console.error(error);
        formikRef.current.setSubmitting(false);
        toast.error("Napotkano błąd podczas połączenia z API");
      });
  };

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <Card minH="125px">
        <Flex direction="column">
          <Formik
            initialValues={initialValues}
            validate={validate}
            // validateOnChange={false}
            // validateOnBlur={false}
            onSubmit={handleSubmit}
            innerRef={formikRef}
          >
            {({ isSubmitting, formik }) => (
              <Form>
                <SimpleGrid
                  columns={{ sm: 1, md: 2, xl: 2 }}
                  spacing="24px"
                  mb="20px"
                >
                  <Field name="dateTime">
                    {({ field, form }) => (
                      <FormControl
                        id="dateTime"
                        isRequired
                        isInvalid={
                          form.errors.dateTime && form.touched.dateTime
                        }
                      >
                        <FormLabel>Data i godzina</FormLabel>
                        <Input
                          type="datetime-local"
                          {...field}
                          placeholder="Data i godzina"
                        />
                        <FormErrorMessage>
                          {form.errors.dateTime}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="location">
                    {({ field, form }) => (
                      <FormControl
                        id="location"
                        isRequired
                        isInvalid={
                          form.errors.location && form.touched.location
                        }
                      >
                        <FormLabel>Lokalizacja</FormLabel>
                        <Creatable
                          {...field}
                          options={locations}
                          placeholder="Wybierz..."
                          styles={darkStyles()}
                          value={form.values[field.name]}
                          onChange={(selectedOptions) => {
                            form.setFieldValue(field.name, selectedOptions);
                          }}
                          formatCreateLabel={(inputValue) =>
                            `Niestandardowa: ${inputValue}`
                          }
                        />
                        <FormErrorMessage>
                          {form.errors.location}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </SimpleGrid>
                <SimpleGrid
                  columns={{ sm: 1, md: 2, xl: 2 }}
                  spacing="24px"
                  mb="20px"
                >
                  <Field name="transferPerson">
                    {({ field, form }) => (
                      <FormControl
                        id="transferPerson"
                        isRequired
                        isInvalid={
                          form.errors.transferPerson &&
                          form.touched.transferPerson
                        }
                      >
                        <FormLabel>Przekazuje</FormLabel>
                        <ReactSelect
                          {...field}
                          noOptionsMessage={() => "Brak dostępnych opcji"}
                          options={[
                            { value: "Właścicielowi", label: "Właścicielowi" },
                            { value: "Zarządcy", label: "Zarządcy" },
                            { value: "Użytkownikowi", label: "Użytkownikowi" },
                            { value: "Policji", label: "Policji" },
                            {
                              value: "Służbie Drogowej",
                              label: "Służbie Drogowej",
                            },
                          ]}
                          placeholder="Wybierz..."
                          styles={darkStyles()}
                          value={form.values.transferPerson}
                          onChange={(selectedOptions) => {
                            form.setFieldValue(field.name, selectedOptions);
                          }}
                        />
                        <FormErrorMessage>
                          {form.errors.transferPerson}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="transferItem">
                    {({ field, form }) => (
                      <FormControl
                        id="transferItem"
                        isRequired
                        isInvalid={
                          (form.errors.transferItem &&
                            form.touched.transferItem) ||
                          (form.errors.transferItemName &&
                            form.touched.transferItemName)
                        }
                      >
                        <FormLabel>
                          Do nadzorowania i zabezpieczenia nastepującego
                        </FormLabel>
                        <Stack direction="row" gap={"20px"}>
                          <RadioGroup name="transferItem" colorScheme="red">
                            <Stack direction="row">
                              <CustomCheckbox
                                key="terenu"
                                label="terenu"
                                isChecked={form.values.transferItem == "terenu"}
                                onChange={() => {
                                  form.setFieldValue(field.name, "terenu");
                                }}
                              />
                              <CustomCheckbox
                                key="obiektu"
                                label="obiektu"
                                isChecked={
                                  form.values.transferItem == "obiektu"
                                }
                                onChange={() => {
                                  form.setFieldValue(field.name, "obiektu");
                                }}
                              />
                              <CustomCheckbox
                                key="mienia"
                                label="mienia"
                                isChecked={form.values.transferItem == "mienia"}
                                onChange={() => {
                                  form.setFieldValue(field.name, "mienia");
                                }}
                              />
                            </Stack>
                          </RadioGroup>
                          {form.values.transferItem && (
                            <Input
                              name="transferItemName"
                              placeholder={`Nazwa ${form.values.transferItem}`}
                              onChange={(e) => {
                                form.setFieldValue(
                                  "transferItemName",
                                  e.target.value
                                );
                              }}
                            />
                          )}
                        </Stack>
                        <FormErrorMessage>
                          {form.errors.transferItem}
                        </FormErrorMessage>
                        <FormErrorMessage>
                          {form.errors.transferItemName}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="transferWarings">
                    {({ field, form }) => (
                      <FormControl
                        id="transformWarings"
                        isRequired
                        isInvalid={
                          form.errors.transformWarings &&
                          form.touched.transformWarings
                        }
                      >
                        <FormLabel>
                          Uwagi szczegółówe dotyczące zabezpieczenia terenu
                          objętego działaniem ratowniczym
                        </FormLabel>
                        <Input {...field} placeholder="Wprowadź..." />
                        <FormErrorMessage>
                          {form.errors.transformWarings}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="transferSeizing">
                    {({ field, form }) => (
                      <FormControl
                        id="transformSeizing"
                        isRequired
                        isInvalid={
                          form.errors.transformSeizing &&
                          form.touched.transformSeizing
                        }
                      >
                        <FormLabel>Przejmujący</FormLabel>
                        <Input {...field} placeholder="Przejmujący" />
                        <FormErrorMessage>
                          {form.errors.transformSeizing}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </SimpleGrid>
                <Button
                  mt={4}
                  colorScheme="red"
                  isLoading={isSubmitting}
                  type="submit"
                >
                  Wyślij
                </Button>
                <Button
                mt={4}
                colorScheme="yellow"
                ml={4}
                as={"a"}
                target="_blank"
                href={"https://discord.com/channels/973322193440231464/1166791796039360512"}
                >
                  Instrukcja
                </Button>
              </Form>
            )}
          </Formik>
        </Flex>
      </Card>
    </Flex>
  );
}

export default Page;
